'use client';
import { useEffect, useRef, useState } from 'react';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Product } from '@curio/contracts';
import { event } from '@curio/client';
import { Button } from '@curio/ui';
import { AppCard } from '../components/products';
export function ProductGrid({ products }: { products: Product[] }) {
  const container = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(4);
  useEffect(() => {
    const update = () =>
      setColumns(
        window.innerWidth <= 760 ? 2 : window.innerWidth <= 1050 ? 3 : 4,
      );
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const virtual = useVirtualizer({
    count: Math.ceil(products.length / columns),
    getScrollElement: () => container.current,
    estimateSize: () => 350,
    overscan: 3,
  });
  if (products.length <= 60)
    return (
      <div className="browse-grid">
        {products.map((p) => (
          <AppCard key={p.id} product={p} />
        ))}
      </div>
    );
  return (
    <div ref={container} style={{ height: '75vh', overflow: 'auto' }}>
      <div style={{ height: virtual.getTotalSize(), position: 'relative' }}>
        {virtual.getVirtualItems().map((row) => (
          <div
            key={row.key}
            data-index={row.index}
            ref={virtual.measureElement}
            className="browse-grid"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${row.start}px)`,
              paddingBottom: 20,
            }}
          >
            {products
              .slice(row.index * columns, (row.index + 1) * columns)
              .map((p) => (
                <AppCard key={p.id} product={p} />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
type Page = { products: Product[]; nextCursor: string | null };
/** "Show more" for any cursor-paged product list. */
export function LoadMore({
  query,
}: {
  query: UseInfiniteQueryResult<InfiniteData<Page>, Error>;
}) {
  if (!query.hasNextPage) return null;
  return (
    <div className="section">
      <Button
        variant="outline"
        disabled={query.isFetchingNextPage}
        onClick={async () => {
          const result = await query.fetchNextPage();
          if (result.isSuccess && result.data)
            event('catalog_page_loaded', undefined, {
              page_number: result.data.pages.length,
              result_count: result.data.pages.at(-1)!.products.length,
            });
        }}
      >
        {query.isFetchingNextPage ? 'Loading…' : 'Show more'}
      </Button>
    </div>
  );
}
